import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import './MyProperties.css';

/**
 * MyProperties Component
 * Displays the owner's hotel registration from hotel_registrations table
 * Shows registration details regardless of status (pending, declined, approved)
 */
const MyPropertiesContent = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editData, setEditData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [categories] = useState([
    '1-star', '2-star', '3-star', '4-star', '5-star',
    'Boutique', 'Budget', 'Luxury', 'Resort', 'Hostel'
  ]);

  useEffect(() => {
    if (currentUser) fetchApprovedHotel();
    // eslint-disable-next-line
  }, [currentUser]);

  const fetchApprovedHotel = async () => {
    setLoading(true);
    try {
      const token = await currentUser.getIdToken();
      console.log('Fetching approved hotel with token:', token);
      const res = await fetch('/api/owner/approved-hotel', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Fetch response status:', res.status);
      if (res.ok) {
        const result = await res.json();
        setHotel(result.data);
        setEditData(result.data);
      } else {
        setHotel(null);
      }
    } catch (e) {
      setHotel(null);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData(prev => ({ ...prev, [name]: value }));
  };


  // Email validation helpers
  const validateEmail = (email) => {
    return /^\S+@\S+\.\S+$/.test(email);
  };


  const [emailError, setEmailError] = useState('');
  const handleManagerEmailChange = (e) => {
    const value = e.target.value;
    setEditData(prev => ({ ...prev, manager_email: value }));
    if (!value) setEmailError('Email required');
    else if (!validateEmail(value)) setEmailError('Invalid email');
    else setEmailError('');
  };


  const handleSave = async (e) => {
    e.preventDefault();
    // Validate manager email before submit
    const email = editData.manager_email || '';
    if (!email) {
      setEmailError('Email required');
      toast.error('Manager email is required.');
      return;
    }
    if (!validateEmail(email)) {
      setEmailError('Invalid email');
      toast.error('Manager email is invalid.');
      return;
    }
    setSaving(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch('/api/owner/approved-hotel', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        const result = await res.json();
        setHotel(result.data);
        setEditData(result.data);
        setEditMode(false);
        toast.success('Hotel details updated!');
      } else {
        toast.error('Failed to update hotel details');
      }
    } catch (e) {
      toast.error('Failed to update hotel details');
    }
    setSaving(false);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Pending Approval', color: '#FFA500', icon: '⏳' },
      approved: { label: 'Approved', color: '#4CAF50', icon: '✓' },
      declined: { label: 'Declined', color: '#f44336', icon: '✗' },
      not_registered: { label: 'Not Registered', color: '#9e9e9e', icon: '○' }
    };

    const config = statusConfig[status] || statusConfig.not_registered;

    return (
      <span
        style={{
          background: config.color,
          color: 'white',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: '600',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <span>{config.icon}</span>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="my-properties-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading your property...</p>
        </div>
      </div>
    );
  }

  if (!hotel && !loading) {
    return (
      <div className="my-properties-page">
        <div className="empty-state">
          <div className="empty-icon">🏨</div>
          <h2>No Approved Hotel</h2>
          <p>You don't have an approved hotel yet.</p>
        </div>
      </div>
    );
  }

  if (loading || !editData) {
    return <div className="my-properties-page"><div className="loading-container"><div className="spinner"></div><p>Loading your property...</p></div></div>;
  }

  return (
    <div className="my-properties-page">
      <div className="page-header">
        <h1>My Approved Hotel</h1>
        {!editMode && <button className="btn btn-primary" onClick={() => setEditMode(true)}>Edit</button>}
      </div>
      <form className="property-form" onSubmit={handleSave}>
        <div className="form-grid">
          <div className="form-group">
            <label>Name</label>
            <input name="name" value={editData.name || ''} onChange={handleChange} disabled={!editMode} required />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select name="category" value={editData.category || ''} onChange={handleChange} disabled={!editMode} required>
              <option value="">Select Category</option>
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea name="description" value={editData.description || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Address</label>
            <input name="address" value={editData.address || ''} onChange={handleChange} disabled={!editMode} required />
          </div>
          <div className="form-group">
            <label>City</label>
            <input name="city" value={editData.city || ''} onChange={handleChange} disabled={!editMode} required />
          </div>
          <div className="form-group">
            <label>State</label>
            <input name="state" value={editData.state || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input name="country" value={editData.country || ''} onChange={handleChange} disabled={!editMode} required />
          </div>
          <div className="form-group">
            <label>Postal Code</label>
            <input name="postal_code" value={editData.postal_code || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input name="phone" value={editData.phone || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input name="email" value={editData.email || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Website</label>
            <input name="website" value={editData.website || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Check-in Time</label>
            <input name="check_in_time" value={editData.check_in_time || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Check-out Time</label>
            <input name="check_out_time" value={editData.check_out_time || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Total Floors</label>
            <input name="total_floors" type="number" value={editData.total_floors || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Total Rooms</label>
            <input name="total_rooms" type="number" value={editData.total_rooms || ''} onChange={handleChange} disabled={!editMode} />
          </div>
          <div className="form-group">
            <label>Manager Email</label>
            <input
              className={`manager-email-input${editMode && emailError ? ' error' : ''}`}
              type="email"
              name="manager_email"
              value={editData.manager_email || ''}
              onChange={handleManagerEmailChange}
              disabled={!editMode}
              placeholder="manager@email.com"
            />
            {editMode && emailError && (
              <div className="manager-email-error">{emailError}</div>
            )}
          </div>
        </div>
        {editMode && (
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
            <button type="button" className="btn btn-secondary" onClick={() => { setEditData(hotel); setEditMode(false); }}>Cancel</button>
          </div>
        )}
      </form>
    </div>
  );
};


export default MyPropertiesContent;
